import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articles } from "@/mocks/articles";
import { places } from "@/mocks/places";
import TableOfContents from "./components/TableOfContents";
import Breadcrumb from "@/components/feature/Breadcrumb";

// ── Likes helpers ─────────────────────────────────────────────
const LIKES_KEY = "articleLikes";

const getLikes = (): Record<string, number> => {
  try {
    return JSON.parse(localStorage.getItem(LIKES_KEY) || "{}");
  } catch {
    return {};
  }
};

const getLikedArticles = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem("likedArticles") || "[]");
  } catch {
    return [];
  }
};

const toggleLike = (articleId: string): { liked: boolean; count: number } => {
  const likes = getLikes();
  const likedArticles = getLikedArticles();
  const idx = likedArticles.indexOf(articleId);
  if (idx === -1) {
    likedArticles.push(articleId);
    likes[articleId] = (likes[articleId] || 0) + 1;
    localStorage.setItem("likedArticles", JSON.stringify(likedArticles));
    localStorage.setItem(LIKES_KEY, JSON.stringify(likes));
    return { liked: true, count: likes[articleId] };
  } else {
    likedArticles.splice(idx, 1);
    likes[articleId] = Math.max(0, (likes[articleId] || 1) - 1);
    localStorage.setItem("likedArticles", JSON.stringify(likedArticles));
    localStorage.setItem(LIKES_KEY, JSON.stringify(likes));
    return { liked: false, count: likes[articleId] };
  }
};

// Base like counts (seed data so articles don't start at 0)
const baseLikeCounts: Record<string, number> = {
  "kapadokya-rehberi": 284,
  "pamukkale-efes-rehberi": 197,
  "turkiye-underrated-yerler": 342,
  "gaziantep-gastronomi": 218,
  "nemrut-gobeklitepe-rehberi": 156,
  "karadeniz-yaylalari": 203,
  "istanbul-gizli-noktalar": 389,
  "ege-adaları-rehberi": 174,
  "istanbul-gezilecek-yerler": 451,
  "sakarya-gezilecek-yerler": 128,
  "antalya-gezilecek-yerler": 267,
  "izmir-gezilecek-yerler": 231,
  "bursa-gezilecek-yerler": 189,
};

// ── Reading List helpers ──────────────────────────────────────
const READING_LIST_KEY = "readingList";

const getReadingList = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(READING_LIST_KEY) || "[]");
  } catch {
    return [];
  }
};

const toggleReadingList = (articleId: string): boolean => {
  const list = getReadingList();
  const idx = list.indexOf(articleId);
  if (idx === -1) {
    list.push(articleId);
    localStorage.setItem(READING_LIST_KEY, JSON.stringify(list));
    return true; // added
  } else {
    list.splice(idx, 1);
    localStorage.setItem(READING_LIST_KEY, JSON.stringify(list));
    return false; // removed
  }
};

const categoryColors: Record<string, string> = {
  "Gezi Rehberi": "bg-amber-100 text-amber-700",
  "Keşif": "bg-emerald-100 text-emerald-700",
  "Gastronomi": "bg-rose-100 text-rose-700",
  "Doğa": "bg-sky-100 text-sky-700",
  "Şehir Rehberi": "bg-violet-100 text-violet-700",
  "Ada & Deniz": "bg-cyan-100 text-cyan-700",
  "Tarih & Arkeoloji": "bg-orange-100 text-orange-700",
};

// Inline images injected between sections
const sectionImages: Record<string, string[]> = {
  "kapadokya-rehberi": [
    "https://readdy.ai/api/search-image?query=Cappadocia%20hot%20air%20balloons%20sunrise%20golden%20hour%20fairy%20chimneys%20Goreme%20valley%20aerial%20view%20stunning%20landscape&width=900&height=500&seq=art-cap-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Cappadocia%20underground%20city%20Derinkuyu%20ancient%20tunnels%20carved%20rock%20interior%20archaeological%20Turkey&width=900&height=500&seq=art-cap-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Cappadocia%20cave%20hotel%20room%20interior%20luxury%20stone%20carved%20bedroom%20romantic%20Goreme%20Turkey&width=900&height=500&seq=art-cap-inline3&orientation=landscape",
  ],
  "pamukkale-efes-rehberi": [
    "https://readdy.ai/api/search-image?query=Pamukkale%20white%20travertine%20terraces%20thermal%20pools%20turquoise%20water%20UNESCO%20Turkey%20aerial%20view%20stunning&width=900&height=500&seq=art-pam-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Ephesus%20Celsus%20Library%20ancient%20ruins%20Turkey%20archaeological%20site%20columns%20facade%20detail&width=900&height=500&seq=art-pam-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Cleopatra%20pool%20Pamukkale%20ancient%20columns%20underwater%20thermal%20water%20swimming%20Turkey&width=900&height=500&seq=art-pam-inline3&orientation=landscape",
  ],
  "turkiye-underrated-yerler": [
    "https://readdy.ai/api/search-image?query=Halfeti%20Turkey%20Euphrates%20river%20boat%20tour%20submerged%20mosque%20minaret%20water%20reflection%20scenic&width=900&height=500&seq=art-und-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Uzungol%20Turkey%20Black%20Sea%20mountain%20lake%20misty%20morning%20fog%20green%20forest%20reflection&width=900&height=500&seq=art-und-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Bozcaada%20Turkey%20Aegean%20island%20vineyard%20winery%20stone%20castle%20blue%20sea%20summer&width=900&height=500&seq=art-und-inline3&orientation=landscape",
  ],
  "gaziantep-gastronomi": [
    "https://readdy.ai/api/search-image?query=Gaziantep%20baklava%20pistachio%20layers%20golden%20syrup%20tray%20traditional%20Turkish%20pastry%20close%20up%20detail&width=900&height=500&seq=art-gaz-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Gaziantep%20kebab%20pistachio%20lamb%20skewer%20grill%20traditional%20Turkish%20cuisine%20authentic%20restaurant&width=900&height=500&seq=art-gaz-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Gaziantep%20historic%20bazaar%20spice%20market%20pistachio%20nuts%20traditional%20Turkish%20market%20colorful&width=900&height=500&seq=art-gaz-inline3&orientation=landscape",
  ],
  "nemrut-gobeklitepe-rehberi": [
    "https://readdy.ai/api/search-image?query=Gobeklitepe%20ancient%20stone%20pillars%20T-shaped%20neolithic%20temple%20Turkey%20archaeological%20excavation%20UNESCO&width=900&height=500&seq=art-nem-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Nemrut%20Mountain%20Turkey%20giant%20stone%20heads%20statues%20sunrise%20golden%20light%20UNESCO%20heritage&width=900&height=500&seq=art-nem-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Sanliurfa%20Turkey%20Balikligol%20sacred%20fish%20pool%20Abraham%20mosque%20reflection%20ancient%20city&width=900&height=500&seq=art-nem-inline3&orientation=landscape",
  ],
  "karadeniz-yaylalari": [
    "https://readdy.ai/api/search-image?query=Ayder%20plateau%20Turkey%20Black%20Sea%20green%20mountains%20wooden%20houses%20mist%20waterfall%20Kackar&width=900&height=500&seq=art-kar-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Karadeniz%20yayla%20trekking%20Kackar%20mountains%20Turkey%20alpine%20meadow%20summer%20hiking%20trail&width=900&height=500&seq=art-kar-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Pokut%20yayla%20Turkey%20Black%20Sea%20wooden%20houses%20panoramic%20mountain%20view%20green%20meadow&width=900&height=500&seq=art-kar-inline3&orientation=landscape",
  ],
  "istanbul-gizli-noktalar": [
    "https://readdy.ai/api/search-image?query=Balat%20Istanbul%20colorful%20houses%20narrow%20streets%20historic%20neighborhood%20authentic%20local%20life&width=900&height=500&seq=art-ist-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Bosphorus%20Istanbul%20Arnavutkoy%20wooden%20yali%20houses%20waterfront%20romantic%20neighborhood&width=900&height=500&seq=art-ist-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Istanbul%20Kadikoy%20market%20bazaar%20fresh%20food%20local%20life%20authentic%20neighborhood%20Turkey&width=900&height=500&seq=art-ist-inline3&orientation=landscape",
  ],
  "ege-adaları-rehberi": [
    "https://readdy.ai/api/search-image?query=Bozcaada%20Turkey%20Aegean%20island%20castle%20harbor%20blue%20sea%20white%20houses%20summer&width=900&height=500&seq=art-ege-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Gokceada%20Turkey%20Aegean%20island%20abandoned%20Greek%20village%20stone%20houses%20Zeytinli&width=900&height=500&seq=art-ege-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Bozcaada%20winery%20vineyard%20Aegean%20Turkey%20wine%20tasting%20summer%20grapes&width=900&height=500&seq=art-ege-inline3&orientation=landscape",
  ],
  "istanbul-gezilecek-yerler": [
    "https://readdy.ai/api/search-image?query=Istanbul%20Hagia%20Sophia%20interior%20golden%20mosaics%20Byzantine%20architecture%20stunning%20dome%20light&width=900&height=500&seq=art-ist-g-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Istanbul%20Bosphorus%20bridge%20sunset%20golden%20hour%20city%20skyline%20minarets%20Turkey&width=900&height=500&seq=art-ist-g-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Istanbul%20Galata%20Tower%20Karakoy%20neighborhood%20streets%20cafes%20modern%20Turkey&width=900&height=500&seq=art-ist-g-inline3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Istanbul%20Princes%20Islands%20Buyukada%20horse%20carriage%20sea%20view%20summer%20Turkey&width=900&height=500&seq=art-ist-g-inline4&orientation=landscape",
  ],
  "sakarya-gezilecek-yerler": [
    "https://readdy.ai/api/search-image?query=Sapanca%20lake%20Turkey%20morning%20mist%20reflection%20green%20forest%20mountains%20peaceful&width=900&height=500&seq=art-sak-g-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Acarlar%20Longozu%20Turkey%20water%20forest%20lotus%20flowers%20wooden%20path%20nature&width=900&height=500&seq=art-sak-g-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Tarakli%20Ottoman%20wooden%20houses%20Turkey%20historic%20village%20narrow%20streets&width=900&height=500&seq=art-sak-g-inline3&orientation=landscape",
  ],
  "antalya-gezilecek-yerler": [
    "https://readdy.ai/api/search-image?query=Antalya%20Kaleici%20old%20town%20harbor%20boats%20Mediterranean%20sea%20historic%20walls%20Turkey&width=900&height=500&seq=art-ant-g-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Kaputas%20beach%20Turkey%20turquoise%20water%20canyon%20cliff%20stairs%20Aegean%20Mediterranean&width=900&height=500&seq=art-ant-g-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Aspendos%20ancient%20theater%20Turkey%20Roman%20ruins%20Antalya%20archaeological%20site&width=900&height=500&seq=art-ant-g-inline3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Alanya%20castle%20Turkey%20Mediterranean%20sea%20cliff%20fortress%20Seljuk%20historic&width=900&height=500&seq=art-ant-g-inline4&orientation=landscape",
  ],
  "izmir-gezilecek-yerler": [
    "https://readdy.ai/api/search-image?query=Izmir%20Kordon%20seafront%20promenade%20sunset%20palm%20trees%20Aegean%20sea%20Turkey&width=900&height=500&seq=art-izm-g-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Alacati%20stone%20houses%20windmills%20Cesme%20Turkey%20Aegean%20summer%20boutique&width=900&height=500&seq=art-izm-g-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Ephesus%20Celsus%20Library%20ancient%20ruins%20Turkey%20archaeological%20site%20UNESCO&width=900&height=500&seq=art-izm-g-inline3&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Sirince%20village%20Turkey%20Izmir%20white%20houses%20vineyard%20wine%20grapes&width=900&height=500&seq=art-izm-g-inline4&orientation=landscape",
  ],
  "kocaeli-gezilecek-yerler": [
    "https://readdy.ai/api/search-image?query=Kartepe%20mountain%20Turkey%20winter%20snow%20forest%20ski%20resort%20cable%20car%20panoramic%20view%20Kocaeli&width=900&height=500&seq=art-koc-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Sapanca%20lake%20Turkey%20morning%20mist%20reflection%20green%20forest%20mountains%20peaceful%20serene&width=900&height=500&seq=art-koc-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Kandira%20Black%20Sea%20coast%20Turkey%20clean%20beach%20sand%20waves%20summer%20nature&width=900&height=500&seq=art-koc-inline3&orientation=landscape",
  ],
  "bolu-gezilecek-yerler": [
    "https://readdy.ai/api/search-image?query=Abant%20lake%20Turkey%20lush%20green%20forest%20mountains%20misty%20morning%20calm%20water%20reflection%20wide%20panoramic&width=900&height=500&seq=art-bol-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Yedig%C3%B6ller%20national%20park%20Turkey%20autumn%20fall%20colors%20seven%20lakes%20forest%20reflection%20stunning&width=900&height=500&seq=art-bol-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Kartalkaya%20ski%20resort%20Turkey%20winter%20snow%20slopes%20cable%20car%20mountain%20forest&width=900&height=500&seq=art-bol-inline3&orientation=landscape",
  ],
  "bursa-gezilecek-yerler": [
    "https://readdy.ai/api/search-image?query=Bursa%20Green%20Mosque%20Yesil%20Cami%20interior%20turquoise%20tiles%20Ottoman%20architecture%20Turkey&width=900&height=500&seq=art-bur-g-inline1&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Uludag%20mountain%20Bursa%20Turkey%20ski%20resort%20snow%20winter%20cable%20car&width=900&height=500&seq=art-bur-g-inline2&orientation=landscape",
    "https://readdy.ai/api/search-image?query=Cumalikizik%20village%20Bursa%20Turkey%20Ottoman%20wooden%20houses%20UNESCO%20narrow%20streets&width=900&height=500&seq=art-bur-g-inline3&orientation=landscape",
  ],
};

// SEO meta helper
const setMetaTag = (name: string, content: string, isProperty = false) => {
  const attr = isProperty ? "property" : "name";
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLinkTag = (rel: string, href: string) => {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const injectSchemaOrg = (id: string, schema: object) => {
  const existing = document.getElementById(`schema-article-${id}`);
  if (existing) existing.remove();
  const script = document.createElement("script");
  script.id = `schema-article-${id}`;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(schema);
  document.head.appendChild(script);
};

// ── Share Bar Component ───────────────────────────────────────
interface ShareBarProps {
  title: string;
}

const ShareBar = ({ title }: ShareBarProps) => {
  const [copied, setCopied] = useState(false);
  const currentUrl = window.location.href;

  const handleCopy = () => {
    navigator.clipboard.writeText(currentUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(currentUrl)}`;
  const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(title + " " + currentUrl)}`;

  return (
    <div className="mt-8 pt-6 border-t border-stone-100">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-stone-400 uppercase tracking-wider">Paylaş</span>
        <a
          href={twitterUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-stone-900 text-white text-xs font-medium rounded-full hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-twitter-x-line"></i>
          <span>Twitter / X</span>
        </a>
        <a
          href={whatsappUrl}
          target="_blank"
          rel="nofollow noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white text-xs font-medium rounded-full hover:bg-emerald-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-whatsapp-line"></i>
          <span>WhatsApp</span>
        </a>
        <button
          onClick={handleCopy}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
            copied
              ? "bg-amber-500 text-white"
              : "bg-stone-100 text-stone-600 hover:bg-stone-200"
          }`}
        >
          <i className={copied ? "ri-check-line" : "ri-link-m"}></i>
          <span>{copied ? "Kopyalandı!" : "Linki Kopyala"}</span>
        </button>
      </div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────

// City → SSS page mapping
const cityFaqLinks: Record<string, { path: string; label: string; icon: string }> = {
  "İstanbul": { path: "/istanbul-sss", label: "İstanbul Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "Nevşehir": { path: "/kapadokya-sss", label: "Kapadokya Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "Antalya": { path: "/antalya-sss", label: "Antalya Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "İzmir": { path: "/izmir-sss", label: "İzmir Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "Mardin": { path: "/mardin-sss", label: "Mardin Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "Rize": { path: "/trabzon-sss", label: "Trabzon & Karadeniz Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "Trabzon": { path: "/trabzon-sss", label: "Trabzon Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "Sakarya": { path: "/sakarya-sss", label: "Sakarya Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "Kocaeli": { path: "/kocaeli-sss", label: "Kocaeli Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "Bolu": { path: "/bolu-sss", label: "Bolu Sık Sorulan Sorular", icon: "ri-question-answer-line" },
  "Gaziantep": { path: "/gaziantep-sss", label: "Gaziantep Sık Sorulan Sorular", icon: "ri-question-answer-line" },
};

// City → places page mapping for "other places in this city" section
const cityPlacesMap: Record<string, string[]> = {
  "İstanbul": [],
  "Nevşehir": ["kapadokya"],
  "Antalya": ["antalya-kaleiçi"],
  "İzmir": ["efes", "sirince", "alacati"],
  "Mardin": ["mardin"],
  "Rize": ["ayder-yaylasi"],
  "Trabzon": ["sumela-manastiri", "trabzon-uzungol"],
  "Sakarya": [],
  "Kocaeli": [],
  "Bolu": [],
  "Gaziantep": ["halfeti"],
  "Bursa": ["bursa-uludag", "iznik"],
  "Muğla": ["bodrum", "olüdeniz"],
  "Adıyaman": ["nemrut-dagi"],
  "Şanlıurfa": ["gobeklitepe", "halfeti"],
  "Kars": ["ani-harabeleri"],
  "Konya": ["konya-mevlana"],
  "Karabük": ["safranbolu"],
  "Edirne": ["edirne-selimiye"],
  "Ağrı": ["ishak-pasa-sarayi"],
  "Van": ["van-akdamar"],
  "Diyarbakır": ["diyarbakir-surlar"],
  "Kırklareli": ["kirklareli-igneada", "trakya-vize"],
  "Denizli": ["pamukkale"],
};

// Per-article SEO data
const articleSEO: Record<string, { title: string; description: string; keywords: string; city?: string; relatedCity?: string }> = {
  "kapadokya-rehberi": {
    title: "Kapadokya'ya İlk Kez Gidenler İçin Eksiksiz Rehber | Türkiye Seyahat",
    description: "Kapadokya'da gezilecek yerler, balon turu fiyatları, en iyi kaya oteller ve gizli vadiler. Göreme, Ürgüp, Derinkuyu yeraltı şehri tam rehberi.",
    keywords: "Kapadokya gezilecek yerler, Kapadokya balon turu, Göreme rehberi, Kapadokya otelleri",
    city: "Nevşehir",
  },
  "pamukkale-efes-rehberi": {
    title: "Pamukkale ve Efes Gezi Rehberi — Ege'nin İki Harikası | Türkiye Seyahat",
    description: "Pamukkale travertenleri, Hierapolis antik kenti, Efes Celsus Kütüphanesi ve Kleopatra Havuzu. Ege'nin iki UNESCO mirası için kapsamlı rehber.",
    keywords: "Pamukkale gezilecek yerler, Efes antik kenti, Pamukkale travertenler, Ege gezi rehberi",
    city: "Denizli",
  },
  "turkiye-underrated-yerler": {
    title: "Türkiye'nin 15 Gizli Destinasyonu — Kalabalıktan Kaçın | Türkiye Seyahat",
    description: "Halfeti, Uzungöl, Bozcaada, Divriği, Kars Ani Harabeleri. Turistlerin henüz keşfetmediği Türkiye'nin en güzel gizli noktaları.",
    keywords: "Türkiye gizli destinasyonlar, Halfeti gezilecek yerler, Bozcaada rehberi, underrated Türkiye",
    city: "Türkiye",
  },
  "gaziantep-gastronomi": {
    title: "Gaziantep'te Yeme-İçme Rehberi — UNESCO Gastronomi Şehri | Türkiye Seyahat",
    description: "Gaziantep baklavası, katmer, beyran çorbası, fıstıklı kebap. UNESCO Gastronomi Şehri Gaziantep'te nerede ne yenir? Tam rehber.",
    keywords: "Gaziantep yemekleri, Gaziantep baklava, Gaziantep kebap, Antep mutfağı rehberi",
    city: "Gaziantep",
    relatedCity: "Gaziantep",
  },
  "nemrut-gobeklitepe-rehberi": {
    title: "Nemrut Dağı ve Göbeklitepe Gezi Rehberi | Türkiye Seyahat",
    description: "12.000 yıllık Göbeklitepe ve 2000 yıllık Nemrut Dağı. Güneydoğu Anadolu'nun iki büyük arkeolojik harikasını nasıl gezeceğinizi anlatan rehber.",
    keywords: "Göbeklitepe gezilecek yerler, Nemrut Dağı rehberi, Şanlıurfa gezi, Adıyaman gezilecek yerler",
    city: "Şanlıurfa",
  },
  "karadeniz-yaylalari": {
    title: "Karadeniz Yaylaları Rehberi — Ayder, Pokut, Uzungöl | Türkiye Seyahat",
    description: "Ayder Yaylası, Pokut, Sal, Elevit. Karadeniz'in en güzel yaylalarında trekking, doğa ve yöresel lezzetler için kapsamlı rehber.",
    keywords: "Karadeniz yaylaları, Ayder Yaylası rehberi, Uzungöl gezilecek yerler, Trabzon yaylalar",
    city: "Rize",
  },
  "istanbul-gizli-noktalar": {
    title: "İstanbul'un Turistlerin Bilmediği 20 Gizli Noktası | Türkiye Seyahat",
    description: "Balat, Kuzguncuk, Arnavutköy, Moda. İstanbul'da turistlerin henüz keşfetmediği gizli semtler, sokaklar ve deneyimler.",
    keywords: "İstanbul gizli noktalar, Balat gezilecek yerler, İstanbul yerel rehberi, İstanbul gizli semtler",
    city: "İstanbul",
  },
  "ege-adaları-rehberi": {
    title: "Bozcaada ve Gökçeada Gezi Rehberi — Türkiye'nin Ege Adaları | Türkiye Seyahat",
    description: "Bozcaada bağları, Gökçeada Zeytinli Köyü, plajlar ve şaraphaneler. Türkiye'nin iki saklı Ege adasında tam rehber.",
    keywords: "Bozcaada gezilecek yerler, Gökçeada rehberi, Ege adaları Türkiye, Bozcaada şarap",
    city: "Çanakkale",
  },
  "istanbul-gezilecek-yerler": {
    title: "İstanbul'da Gezilecek 50 Yer — Kapsamlı Şehir Rehberi | Türkiye Seyahat",
    description: "İstanbul'da gezilecek yerler listesi. Ayasofya, Topkapı Sarayı, Boğaz, Adalar, Balat ve daha fazlası. En kapsamlı İstanbul gezi rehberi.",
    keywords: "İstanbul gezilecek yerler, İstanbul gezi rehberi, İstanbul turistik yerler, İstanbul ne gezilir",
    city: "İstanbul",
  },
  "sakarya-gezilecek-yerler": {
    title: "Sakarya'da Gezilecek Yerler — Sapanca Gölü ve Doğa Rotaları | Türkiye Seyahat",
    description: "Sakarya'da gezilecek yerler. Sapanca Gölü, Acarlar Longozu, Taraklı Osmanlı evleri ve yaylalar. Marmara'nın doğa cenneti için tam rehber.",
    keywords: "Sakarya gezilecek yerler, Sapanca Gölü rehberi, Acarlar Longozu, Taraklı gezilecek yerler",
    city: "Sakarya",
    relatedCity: "Sakarya",
  },
  "antalya-gezilecek-yerler": {
    title: "Antalya'da Gezilecek 40 Yer — Akdeniz'in Başkenti Rehberi | Türkiye Seyahat",
    description: "Antalya'da gezilecek yerler. Kaleiçi, Phaselis, Kaş, Alanya, Termessos, Köprülü Kanyon. Türkiye'nin turizm başkentinde tam rehber.",
    keywords: "Antalya gezilecek yerler, Antalya gezi rehberi, Kaş gezilecek yerler, Alanya rehberi",
    city: "Antalya",
  },
  "izmir-gezilecek-yerler": {
    title: "İzmir'de Gezilecek 30 Yer — Ege'nin İncisi Rehberi | Türkiye Seyahat",
    description: "İzmir'de gezilecek yerler. Kemeraltı, Efes, Alaçatı, Şirince, Urla bağ yolu. Ege'nin başkentinde kapsamlı gezi rehberi.",
    keywords: "İzmir gezilecek yerler, İzmir gezi rehberi, Alaçatı rehberi, Efes antik kenti",
    city: "İzmir",
  },
  "bursa-gezilecek-yerler": {
    title: "Bursa'da Gezilecek 25 Yer — Osmanlı'nın İlk Başkenti Rehberi | Türkiye Seyahat",
    description: "Bursa'da gezilecek yerler. Ulu Cami, Uludağ, Cumalıkızık, İznik, Karacabey Longozu. Yeşil Bursa'nın en güzel noktaları.",
    keywords: "Bursa gezilecek yerler, Bursa gezi rehberi, Uludağ rehberi, Cumalıkızık gezilecek yerler",
    city: "Bursa",
  },
  "ankara-gezilecek-yerler": {
    title: "Ankara'da Gezilecek Yerler — Başkentin Tarihi ve Doğası | Türkiye Seyahat",
    description: "Ankara'da gezilecek yerler. Anıtkabir, Anadolu Medeniyetleri Müzesi, Hamamönü, Eymir Gölü. Türkiye'nin başkentinde tam rehber.",
    keywords: "Ankara gezilecek yerler, Ankara gezi rehberi, Anıtkabir ziyareti, Hamamönü gezilecek yerler",
    city: "Ankara",
  },
  "trabzon-gezilecek-yerler": {
    title: "Trabzon'da Gezilecek Yerler — Sümela ve Karadeniz Rehberi | Türkiye Seyahat",
    description: "Trabzon'da gezilecek yerler. Sümela Manastırı, Uzungöl, Boztepe, Trabzon Ayasofyası. Karadeniz'in efsanevi şehrinde tam rehber.",
    keywords: "Trabzon gezilecek yerler, Sümela Manastırı, Uzungöl rehberi, Trabzon gezi rehberi",
    city: "Trabzon",
  },
  "konya-gezilecek-yerler": {
    title: "Konya'da Gezilecek Yerler — Mevlana Şehrinde Manevi Yolculuk | Türkiye Seyahat",
    description: "Konya'da gezilecek yerler. Mevlana Müzesi, Çatalhöyük, Sille Köyü, Beyşehir Gölü. İç Anadolu'nun manevi başkentinde tam rehber.",
    keywords: "Konya gezilecek yerler, Mevlana Müzesi, Konya gezi rehberi, Çatalhöyük ziyareti",
    city: "Konya",
  },
  "gaziantep-gezilecek-yerler": {
    title: "Gaziantep'te Gezilecek Yerler — Zeugma ve Tarihi Çarşılar | Türkiye Seyahat",
    description: "Gaziantep'te gezilecek yerler. Zeugma Mozaik Müzesi, Gaziantep Kalesi, Halfeti, Bakırcılar Çarşısı. UNESCO şehrinde tam rehber.",
    keywords: "Gaziantep gezilecek yerler, Zeugma Müzesi, Halfeti gezilecek yerler, Gaziantep gezi rehberi",
    city: "Gaziantep",
    relatedCity: "Gaziantep",
  },
  "kocaeli-gezilecek-yerler": {
    title: "Kocaeli'de Gezilecek Yerler — Sapanca ve Kartepe Rehberi | Türkiye Seyahat",
    description: "Kocaeli'de gezilecek yerler. Sapanca Gölü, Kartepe kayak merkezi, İzmit Körfezi ve doğa rotaları. Marmara'nın sanayi şehrinin gizli güzellikleri.",
    keywords: "Kocaeli gezilecek yerler, Kartepe rehberi, Sapanca Gölü, İzmit gezilecek yerler, Seka Park, Hereke halısı, Kandıra sahili, Kocaeli hafta sonu kaçamak, Kocaeli doğa yürüyüşü, İzmit pişmaniyesi, Kocaeli kış tatili",
    city: "Kocaeli",
    relatedCity: "Kocaeli",
  },
  "bolu-gezilecek-yerler": {
    title: "Bolu'da Gezilecek Yerler — Abant Gölü ve Yedigöller Rehberi | Türkiye Seyahat",
    description: "Bolu'da gezilecek yerler. Abant Gölü, Yedigöller Milli Parkı, Kartalkaya kayak merkezi ve orman yürüyüşleri. Batı Karadeniz'in doğa cenneti.",
    keywords: "Bolu gezilecek yerler, Abant Gölü rehberi, Yedigöller Milli Parkı, Kartalkaya kayak, Mudurnu Osmanlı evleri, Göynük tarihi, Bolu tereyağı, Bolu hafta sonu kaçamak, Bolu doğa yürüyüşü, Bolu kış tatili, Bolu sonbahar",
    city: "Bolu",
    relatedCity: "Bolu",
  },
  "mardin-gezilecek-yerler": {
    title: "Mardin'de Gezilecek Yerler — Taşın ve Tarihin Şehri Rehberi | Türkiye Seyahat",
    description: "Mardin'de gezilecek yerler. Deyrulzafaran Manastırı, Mor Gabriel, Midyat, Dara Antik Kenti. Mezopotamya'nın eşsiz şehrinde tam rehber.",
    keywords: "Mardin gezilecek yerler, Deyrulzafaran Manastırı, Mardin gezi rehberi, Midyat gezilecek yerler",
    city: "Mardin",
  },
  "kapadokya-gezilecek-yerler": {
    title: "Kapadokya'da Gezilecek Yerler — Peri Bacaları ve Vadiler | Türkiye Seyahat",
    description: "Kapadokya'da gezilecek yerler. Göreme Açık Hava Müzesi, Derinkuyu, Ihlara Vadisi, Uçhisar. Nevşehir'in eşsiz coğrafyasında tam rehber.",
    keywords: "Kapadokya gezilecek yerler, Göreme rehberi, Derinkuyu yeraltı şehri, Ihlara Vadisi",
    city: "Nevşehir",
  },
};

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lightboxImg, setLightboxImg] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeAnim, setLikeAnim] = useState(false);
  const [toast, setToast] = useState<{ visible: boolean; message: string; type: "add" | "remove" | "like" }>({
    visible: false,
    message: "",
    type: "add",
  });

  useEffect(() => {
    if (id) {
      setIsSaved(getReadingList().includes(id));
      const liked = getLikedArticles().includes(id);
      setIsLiked(liked);
      const storedLikes = getLikes();
      const base = baseLikeCounts[id] || 0;
      const stored = storedLikes[id] || 0;
      // If user has liked, stored count already includes their like
      setLikeCount(base + stored);
    }
  }, [id]);

  const showToast = useCallback((message: string, type: "add" | "remove" | "like") => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((t) => ({ ...t, visible: false })), 2500);
  }, []);

  const handleToggleLike = useCallback(() => {
    if (!id) return;
    const base = baseLikeCounts[id] || 0;
    const result = toggleLike(id);
    setIsLiked(result.liked);
    setLikeCount(base + result.count);
    if (result.liked) {
      setLikeAnim(true);
      setTimeout(() => setLikeAnim(false), 600);
      showToast("Bu makaleyi beğendiniz!", "like");
    }
  }, [id, showToast]);

  const handleToggleSave = useCallback(() => {
    if (!id) return;
    const added = toggleReadingList(id);
    setIsSaved(added);
    showToast(
      added ? "Okuma listesine eklendi" : "Okuma listesinden çıkarıldı",
      added ? "add" : "remove"
    );
  }, [id, showToast]);

  const article = articles.find((a) => a.id === id);
  const relatedArticles = articles
    .filter((a) => a.id !== id && (a.category === article?.category || a.tags.some((t) => article?.tags.includes(t))))
    .slice(0, 3);

  const inlineImages = sectionImages[id || ""] || [];

  // SEO: update meta tags per article
  useEffect(() => {
    if (!article || !id) return;
    const seo = articleSEO[id];
    const pageTitle = seo?.title || `${article.title} | Türkiye Seyahat Rehberi`;
    const pageDesc = seo?.description || article.shortDesc;
    const pageKeywords = seo?.keywords || article.tags.join(", ");
    const canonicalUrl = `https://geziturkiyem.com/article/${id}`;
    const imageUrl = article.image;

    document.title = pageTitle;
    setMetaTag("description", pageDesc);
    setMetaTag("keywords", pageKeywords);
    setMetaTag("robots", "index, follow");
    setLinkTag("canonical", canonicalUrl);

    // Open Graph
    setMetaTag("og:title", pageTitle, true);
    setMetaTag("og:description", pageDesc, true);
    setMetaTag("og:image", imageUrl, true);
    setMetaTag("og:image:width", "800", true);
    setMetaTag("og:image:height", "450", true);
    setMetaTag("og:image:alt", pageTitle, true);
    setMetaTag("og:image:type", "image/jpeg", true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("og:type", "article", true);
    setMetaTag("og:locale", "tr_TR", true);
    setMetaTag("og:site_name", "Yerel Bilgi — Türkiye Seyahat Rehberi", true);

    // Twitter Card
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:site", "@geziturkiyem");
    setMetaTag("twitter:creator", "@geziturkiyem");
    setMetaTag("twitter:title", pageTitle);
    setMetaTag("twitter:description", pageDesc);
    setMetaTag("twitter:image", imageUrl);
    setMetaTag("twitter:image:alt", pageTitle);

    // Article meta
    setMetaTag("article:published_time", article.date, true);
    setMetaTag("article:author", article.author, true);
    setMetaTag("article:section", article.category, true);

    // Geo tags if city known
    if (seo?.city) {
      setMetaTag("geo.placename", seo.city);
      setMetaTag("geo.region", "TR");
    }

    // Schema.org Article
    const wordCount = article.content.trim().split(/\s+/).length;
    const schema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": pageDesc,
      "image": {
        "@type": "ImageObject",
        "url": imageUrl,
        "width": 800,
        "height": 450,
      },
      "author": {
        "@type": "Person",
        "name": article.author,
        "url": "https://geziturkiyem.com/articles",
      },
      "publisher": {
        "@type": "Organization",
        "name": "Yerel Bilgi — Türkiye Seyahat Rehberi",
        "url": "https://geziturkiyem.com",
        "logo": {
          "@type": "ImageObject",
          "url": "https://geziturkiyem.com/vite.svg",
        },
      },
      "datePublished": article.date,
      "dateModified": article.date,
      "url": canonicalUrl,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": canonicalUrl,
      },
      "keywords": pageKeywords,
      "articleSection": article.category,
      "inLanguage": "tr-TR",
      "wordCount": wordCount,
      "potentialAction": {
        "@type": "ReadAction",
        "target": canonicalUrl,
      },
      ...(seo?.city && {
        "contentLocation": {
          "@type": "Place",
          "name": seo.city,
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "TR",
            "addressLocality": seo.city,
          },
        },
      }),
    };
    injectSchemaOrg(id, schema);

    // BreadcrumbList schema
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Ana Sayfa",
          "item": "https://geziturkiyem.com/",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Makaleler",
          "item": "https://geziturkiyem.com/articles",
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": article.title,
          "item": canonicalUrl,
        },
      ],
    };
    const existingBreadcrumb = document.getElementById(`schema-breadcrumb-${id}`);
    if (existingBreadcrumb) existingBreadcrumb.remove();
    const breadcrumbScript = document.createElement("script");
    breadcrumbScript.id = `schema-breadcrumb-${id}`;
    breadcrumbScript.type = "application/ld+json";
    breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(breadcrumbScript);

    // Cleanup on unmount
    return () => {
      document.title = "Türkiye Seyahat Rehberi";
      const schemaEl = document.getElementById(`schema-article-${id}`);
      if (schemaEl) schemaEl.remove();
      const breadcrumbEl = document.getElementById(`schema-breadcrumb-${id}`);
      if (breadcrumbEl) breadcrumbEl.remove();
    };
  }, [id, article]);

  if (!article) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <p className="text-stone-400 mb-4">Makale bulunamadı.</p>
        <button onClick={() => navigate("/articles")} className="text-amber-600 font-medium cursor-pointer">Geri Dön</button>
      </div>
    </div>
  );

  // Calculate reading time and word count
  const getReadingStats = (content: string) => {
    const words = content.trim().split(/\s+/).length;
    const readingTime = Math.max(1, Math.ceil(words / 200));
    return { words, readingTime };
  };

  const renderContent = (content: string) => {
    const lines = content.split("\n");
    let h2Count = 0;
    const elements: JSX.Element[] = [];

    lines.forEach((line, i) => {
      if (line.startsWith("## ")) {
        // Inject inline image before every 2nd and 4th h2
        if (h2Count > 0 && h2Count < inlineImages.length) {
          elements.push(
            <div key={`img-${h2Count}`} className="my-8 rounded-2xl overflow-hidden cursor-pointer group relative" onClick={() => setLightboxImg(inlineImages[h2Count - 1])}>
              <img
                src={inlineImages[h2Count - 1]}
                alt="Makale görseli"
                className="w-full h-64 object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                <div className="w-10 h-10 flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                  <i className="ri-zoom-in-line text-stone-700"></i>
                </div>
              </div>
            </div>
          );
        }
        h2Count++;
        const headingText = line.replace("## ", "");
        const headingId = headingText
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^a-z0-9\u00C0-\u024F-]/g, "")
          .slice(0, 50);
        elements.push(
          <h2
            key={i}
            data-toc-id={headingId}
            className="text-xl font-bold text-stone-900 mt-8 mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {headingText}
          </h2>
        );
      } else if (line.startsWith("**") && line.endsWith("**")) {
        elements.push(<p key={i} className="font-semibold text-stone-800 mt-4 mb-1">{line.replace(/\*\*/g, "")}</p>);
      } else if (line.startsWith("- ")) {
        elements.push(<li key={i} className="text-stone-600 text-sm ml-4 list-disc leading-relaxed">{line.replace("- ", "")}</li>);
      } else if (line.trim() === "") {
        elements.push(<br key={i} />);
      } else {
        elements.push(<p key={i} className="text-stone-600 text-sm leading-relaxed">{line}</p>);
      }
    });

    return elements;
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="relative h-[55vh] overflow-hidden">
        <img src={article.image} alt={article.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent"></div>
        <button
          onClick={() => navigate("/articles")}
          className="absolute top-24 left-6 flex items-center gap-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm px-4 py-2 rounded-full cursor-pointer hover:bg-white/30 transition-colors whitespace-nowrap"
        >
          <i className="ri-arrow-left-line"></i> Geri
        </button>

        {/* Save to Reading List Button */}
        <button
          onClick={handleToggleSave}
          className={`absolute top-24 right-6 flex items-center gap-2 backdrop-blur-sm border text-sm px-4 py-2 rounded-full cursor-pointer transition-all whitespace-nowrap ${
            isSaved
              ? "bg-amber-500/90 border-amber-400 text-white"
              : "bg-white/20 border-white/30 text-white hover:bg-white/30"
          }`}
        >
          <i className={isSaved ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
          <span className="hidden sm:inline">{isSaved ? "Listede" : "Listeye Ekle"}</span>
        </button>
        <div className="absolute bottom-8 left-0 right-0 px-6 md:px-16">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColors[article.category] || "bg-stone-100 text-stone-600"}`}>
                {article.category}
              </span>
              <div className="flex items-center gap-1 text-white/70 text-xs">
                <i className="ri-time-line"></i>
                <span>{article.readTime}</span>
              </div>
              {article.featured && (
                <span className="flex items-center gap-1 bg-amber-500/90 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                  <i className="ri-fire-line"></i> Öne Çıkan
                </span>
              )}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {article.title}
            </h1>
            <div className="flex items-center gap-4 mt-3 text-white/70 text-xs">
              <span>{article.author}</span>
              <span>{new Date(article.date).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: "Ana Sayfa", href: "/" },
            { label: "Makaleler", href: "/articles" },
            { label: article.title },
          ]}
          className="mb-6 text-stone-400"
        />

        <p className="text-stone-500 text-base leading-relaxed mb-8 border-l-4 border-amber-400 pl-4 italic">
          {article.shortDesc}
        </p>

        {/* Reading Time & Table of Contents */}
        {(() => {
          const { words, readingTime } = getReadingStats(article.content);
          return (
            <TableOfContents
              content={article.content}
              readingTime={readingTime}
              wordCount={words}
            />
          );
        })()}

        {/* Inline image grid at top */}
        {inlineImages.length > 0 && (
          <div className="grid grid-cols-3 gap-2 mb-8 rounded-2xl overflow-hidden">
            {inlineImages.map((img, idx) => (
              <div
                key={idx}
                className="relative h-32 overflow-hidden cursor-pointer group"
                onClick={() => setLightboxImg(img)}
              >
                <img src={img} alt={`Görsel ${idx + 1}`} className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <div className="w-8 h-8 flex items-center justify-center bg-white/80 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-zoom-in-line text-stone-700 text-sm"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="space-y-1">{renderContent(article.content)}</div>

        {/* City FAQ Internal Link Box */}
        {(() => {
          const seo = articleSEO[id || ""];
          const faqLink = seo?.city ? cityFaqLinks[seo.city] : null;
          if (!faqLink) return null;
          return (
            <div className="mt-10 p-5 bg-amber-50 border border-amber-200 rounded-2xl flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                <i className="ri-question-answer-line text-amber-600 text-lg"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-1">Merak Ettikleriniz</p>
                <p className="text-stone-700 text-sm mb-3 leading-relaxed">
                  Bu şehir hakkında sık sorulan soruları ve pratik bilgileri derledik.
                </p>
                <Link
                  to={faqLink.path}
                  className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-right-circle-line"></i>
                  {faqLink.label}
                </Link>
              </div>
            </div>
          );
        })()}

        {/* Tags */}
        <div className="mt-12 pt-8 border-t border-stone-100">
          <div className="flex flex-wrap gap-2">
            {article.tags.map((tag) => (
              <span key={tag} className="bg-stone-100 text-stone-600 text-xs px-3 py-1 rounded-full">{tag}</span>
            ))}
          </div>
        </div>

        {/* Social Share */}
        <ShareBar title={article.title} />

        {/* Like Button */}
        <div className="mt-6 flex items-center gap-4">
          <button
            onClick={handleToggleLike}
            className={`group flex items-center gap-3 px-6 py-3 rounded-2xl border-2 font-medium text-sm transition-all duration-300 cursor-pointer whitespace-nowrap
              ${isLiked
                ? "border-rose-400 bg-rose-50 text-rose-600"
                : "border-stone-200 bg-white text-stone-500 hover:border-rose-300 hover:text-rose-500 hover:bg-rose-50"
              }`}
          >
            <span
              className={`w-5 h-5 flex items-center justify-center transition-transform duration-300 ${likeAnim ? "scale-150" : "scale-100"}`}
            >
              <i className={isLiked ? "ri-heart-fill text-rose-500" : "ri-heart-line"}></i>
            </span>
            <span>{isLiked ? "Beğendiniz" : "Bu makaleyi beğendim"}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-bold transition-colors ${isLiked ? "bg-rose-100 text-rose-600" : "bg-stone-100 text-stone-500"}`}>
              {likeCount.toLocaleString("tr-TR")}
            </span>
          </button>
          {isLiked && (
            <span className="text-xs text-stone-400 animate-pulse">Teşekkürler! Geri bildiriminiz değerli.</span>
          )}
        </div>

        {/* Author card */}
        <div className="mt-8 p-5 bg-stone-50 rounded-2xl flex items-center gap-4">
          <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
            <i className="ri-user-line text-amber-600 text-xl"></i>
          </div>
          <div>
            <p className="font-semibold text-stone-800 text-sm">{article.author}</p>
            <p className="text-stone-400 text-xs mt-0.5">Seyahat Editörü · Türkiye Uzmanı</p>
          </div>
        </div>
      </div>

      {/* Related Places in Same City */}
      {(() => {
        const seo = articleSEO[id || ""];
        const cityKey = seo?.city || seo?.relatedCity;
        if (!cityKey) return null;
        const placeIds = cityPlacesMap[cityKey] || [];
        const cityPlaces = places.filter((p) => placeIds.includes(p.id)).slice(0, 4);
        if (cityPlaces.length === 0) return null;
        return (
          <section className="bg-amber-50/60 py-14">
            <div className="max-w-5xl mx-auto px-4 md:px-8">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-amber-500 rounded-full"></div>
                <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {cityKey}&apos;da Gezilecek Diğer Yerler
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {cityPlaces.map((p) => (
                  <Link
                    key={p.id}
                    to={`/place/${p.id}`}
                    className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-amber-300 hover:scale-[1.01] transition-all duration-200 block"
                  >
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={p.image}
                        alt={p.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-0.5">
                        <i className="ri-star-fill text-amber-400 text-xs"></i>
                        <span className="text-white text-xs font-semibold">{p.rating}</span>
                      </div>
                      <div className="absolute bottom-2 left-2 right-2">
                        <p className="text-white font-bold text-sm leading-tight line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {p.name}
                        </p>
                        <p className="text-white/70 text-xs mt-0.5">{p.city}</p>
                      </div>
                    </div>
                    <div className="p-3">
                      <div className="flex flex-wrap gap-1 mb-2">
                        {p.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-full">{tag}</span>
                        ))}
                      </div>
                      <p className="text-stone-500 text-xs line-clamp-2 leading-relaxed">{p.shortDesc}</p>
                      <div className="flex items-center gap-1 mt-2 text-amber-600 text-xs font-medium">
                        <span>Keşfet</span>
                        <i className="ri-arrow-right-line"></i>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-6 text-center">
                <Link
                  to="/places"
                  className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-amber-600 transition-colors cursor-pointer"
                >
                  Tüm mekanları keşfet
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </section>
        );
      })()}

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-stone-50 py-14">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-6 bg-amber-500 rounded-full"></div>
              <h2 className="text-xl font-bold text-stone-900" style={{ fontFamily: "'Playfair Display', serif" }}>
                İlgili Makaleler
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.id}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-stone-100 hover:border-amber-200 hover:scale-[1.01] transition-all duration-200 block"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={rel.image}
                      alt={rel.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                    <div className="absolute top-3 left-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColors[rel.category] || "bg-stone-100 text-stone-600"}`}>
                        {rel.category}
                      </span>
                    </div>
                    <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/40 backdrop-blur-sm rounded-full px-2 py-1">
                      <i className="ri-time-line text-white/80 text-xs"></i>
                      <span className="text-white text-xs">{rel.readTime}</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-stone-900 text-sm leading-snug mb-2 line-clamp-2 group-hover:text-amber-700 transition-colors" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {rel.title}
                    </h3>
                    <p className="text-stone-400 text-xs line-clamp-2">{rel.shortDesc}</p>
                    <div className="flex items-center gap-1 mt-3 text-amber-600 text-xs font-medium">
                      <span>Devamını Oku</span>
                      <i className="ri-arrow-right-line"></i>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Toast Notification */}
      <div
        className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 px-5 py-3 rounded-full text-sm font-medium shadow-lg transition-all duration-300 ${
          toast.visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"
        } ${toast.type === "like" ? "bg-rose-500 text-white" : toast.type === "add" ? "bg-amber-500 text-white" : "bg-stone-700 text-white"}`}
      >
        <i className={toast.type === "like" ? "ri-heart-fill" : toast.type === "add" ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
        {toast.message}
      </div>

      {/* Lightbox */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <button
            className="absolute top-5 right-5 w-10 h-10 flex items-center justify-center bg-white/20 rounded-full text-white hover:bg-white/30 transition-colors cursor-pointer"
            onClick={() => setLightboxImg(null)}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <img
            src={lightboxImg}
            alt="Büyük görsel"
            className="max-w-4xl w-full max-h-[85vh] object-contain rounded-xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default ArticleDetail;
